import { AttendanceGuage, MainContainer } from '@wot/components';
import { ITrialClass } from '@wot/typings';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LOADING } from '../../../../assets/animated';
import { DEFAULT_PROFILE_PIC } from '../../../../assets/icons';
import { StudentPlan } from '../../../../interfaces/Plan';
import xhr from '../../../../utils/fetch';
import { getAttendacePercentage } from '../../../../utils/functions';

export interface IPlanAttendance {
  _id: string;
  sessions: ITrialClass[];
  plan: StudentPlan;
}

export default function StudentAttendance() {
  const { student_id } = useParams();
  const [totalSessions, setTotalSessions] = useState<ITrialClass[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    xhr({
      url: `/u/${student_id}/sessions`,
      method: 'GET',
      params: {
        type: 'plan',
        status: 'completed'
      }
    })
      .then((res) => {
        if (res.status === 'OK') {
          setTotalSessions(res.data);
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  return (
    <MainContainer>
      <div className="w-full flex flex-col">
        <div className="w-full flex flex-col md:flex-row justify-center md:justify-start items-center gap-7 bg-white p-7 rounded-2xl">
          <h1 className="md:hidden text-lg font-semibold mr-auto">My Attendance</h1>
          {loading ? (
            <div className="w-full min-h-[164px] flex items-center justify-center">
              <img src={LOADING} className="w-14 h-14" />
            </div>
          ) : (
            <>
              <div className="w-[164px] md:w-[128px]">
                <AttendanceGuage
                  percentage={getAttendacePercentage(
                    totalSessions.filter((i) => i.attendance && i.attendance.student === 'present').length,
                    totalSessions.length
                  )}
                />
              </div>
              <div className="hidden w-full md:flex flex-col gap-4">
                <h1 className="text-lg font-semibold">Overall Attendance</h1>
                <p className="text-sm text-neutralGray2">
                  {totalSessions.filter((i) => i.attendance && i.attendance.student === 'present').length}/
                  {totalSessions.length} Completed Lessons
                </p>
              </div>
            </>
          )}
        </div>
        <div className="w-full flex flex-col gap-7 bg-white p-7 rounded-2xl my-10">
          <h1 className="text-2xl font-semibold">Select the Plan to view the attendance</h1>
          <PlansAttendance />
        </div>
      </div>
    </MainContainer>
  );
}

function PlansAttendance() {
  const { student_id } = useParams();
  const [plans, setPlans] = useState<IPlanAttendance[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    xhr({
      url: `/u/${student_id}/attendance`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          setPlans(res.data);
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="w-full min-h-[164px] flex items-center justify-center">
        <img src={LOADING} className="w-14 h-14" />
      </div>
    );
  }

  return (
    <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-7">
      {plans.map((plan) => (
        <PlanAttendanceCard key={plan._id} plan={plan} />
      ))}
    </div>
  );
}

export function PlanAttendanceCard({ plan }: { plan: IPlanAttendance }) {
  const { student_id } = useParams();
  if (!plan) {
    return null;
  }
  return (
    <div className="w-full border-l-4 border border-primary bg-white rounded-md p-4 md:p-7 flex flex-col gap-7">
      <div className="flex flex-col md:flex-row  gap-4 justify-between">
        <div className="flex flex-col gap-4">
          <h1 className="text-sm md:text-lg text-primary font-medium">Plan Id: {plan._id}</h1>

          <div className="flex flex-row-reverse justify-end md:justify-start items-center md:items-start md:flex-col gap-2">
            <p className="text-2xl font-bold">
              {plan.sessions.filter((session) => session.status === 'completed').length}/{plan.plan.totalSessions}
            </p>
            <p className="text-sm text-neutralGray2 font-medium">Completed classes</p>
          </div>
        </div>

        <div className="flex md:flex-col items-center gap-2">
          <div className="h-10 w-10 md:h-16 md:w-16 rounded-full overflow-hidden">
            <img
              src={plan.plan.tutor.profilePic || DEFAULT_PROFILE_PIC}
              alt="tutor_profile_pic"
              className="w-full h-full object-cover"
            />
          </div>
          <span className="text-sm md:text-base font-normal">{plan.plan.tutor.name}</span>
        </div>
      </div>

      <div className="w-full border-t pt-4 flex items-center gap-4">
        <div className="w-[64px]">
          <AttendanceGuage
            percentage={getAttendacePercentage(
              plan.sessions.filter((i) => i.attendance && i.attendance.student === 'present').length,
              plan.plan.totalSessions
            )}
          />
        </div>
        <Link to={`/student-management/${student_id}/plans/${plan._id}/attendance`}>
          <h3 className="text-sm md:text-base text-primary underline underline-offset-1 font-medium cursor-pointer">
            View Detailed Attendance
          </h3>
        </Link>
      </div>
    </div>
  );
}
